import { Injectable, inject } from '@angular/core';
import { Analytics, logEvent, setUserId, setUserProperties } from '@angular/fire/analytics';
import { IS_NATIVE } from '../shared/platform.util';

// Dimensión propia para separar Libre Salud de Cardio dentro de la misma
// propiedad GA4 (además del flujo de datos, que ya los separa por App ID).
const APP_CONTEXT = 'libresalud';

type NativeAnalyticsApi = typeof import('@capacitor-firebase/analytics').FirebaseAnalytics;

/**
 * Analítica unificada para los 3 contextos de Libre Salud:
 *  - Web / PWA (navegador real) -> SDK JS de Firebase vía @angular/fire (flujo "pwa").
 *  - Android / iOS nativo (WebView) -> plugin @capacitor-firebase/analytics
 *    (flujos "Android" / "iOS"), evitando así contaminar el flujo web.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  // En nativo no se provee (no hay provideAnalytics) -> queda null.
  private web = inject(Analytics, { optional: true });
  private native: NativeAnalyticsApi | null = null;
  private ready = false;

  /** Se llama una vez al arranque (provideAppInitializer). No bloquea. */
  async init(): Promise<void> {
    if (this.ready) return;
    try {
      if (IS_NATIVE) {
        const mod = await import('@capacitor-firebase/analytics');
        this.native = mod.FirebaseAnalytics;
        await this.native.setEnabled({ enabled: true });
        await this.native.setUserProperty({ key: 'app_context', value: APP_CONTEXT });
      } else if (this.web) {
        setUserProperties(this.web, { app_context: APP_CONTEXT });
      }
      this.ready = true;
    } catch (e) {
      console.warn('AnalyticsService.init falló (no bloquea el arranque):', e);
    }
  }

  /** Asocia el uid de Firebase al usuario en GA4. Pasar null al cerrar sesión. */
  async setUser(uid: string | null): Promise<void> {
    try {
      if (IS_NATIVE) {
        await this.native?.setUserId({ userId: uid });
      } else if (this.web) {
        setUserId(this.web, uid as string);
      }
    } catch (e) {
      console.warn('AnalyticsService.setUser falló:', e);
    }
  }

  /** Registra un evento personalizado. */
  async logEvent(name: string, params: Record<string, unknown> = {}): Promise<void> {
    const merged = { app_context: APP_CONTEXT, ...params };
    try {
      if (IS_NATIVE) {
        await this.native?.logEvent({ name, params: merged });
      } else if (this.web) {
        logEvent(this.web, name, merged);
      }
    } catch (e) {
      console.warn(`AnalyticsService.logEvent(${name}) falló:`, e);
    }
  }

  /**
   * Marca la pantalla actual. Solo nativo: en web lo cubre automáticamente
   * ScreenTrackingService de @angular/fire (evita screen_view duplicados).
   */
  async setScreen(screenName: string): Promise<void> {
    if (!IS_NATIVE) return;
    try {
      await this.native?.setCurrentScreen({ screenName });
    } catch (e) {
      console.warn('AnalyticsService.setScreen falló:', e);
    }
  }
}
